import { createApi, CreateApiOptions, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { IMsalContext, useMsal } from '@azure/msal-react';
import { getAuthTokens, getClaimsAsync, loginRequest } from './auth';
import { axiosBaseQuery } from './AIMApi/controllers/hooksServices/baseQueries/axiosBaseQuery';
import { getSimulatedUser, getSimulatedRole } from './simulationService';
import { useEffect, useState } from 'react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, EndpointDefinitions } from '@reduxjs/toolkit/query/react';

/** The base query signature used by all ARI API services. */
type AriBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

interface AriApiQueryCreatorProps<
  Definitions extends EndpointDefinitions = EndpointDefinitions,
  ReducerPath extends string = string,
  TagTypes extends string = string
> {
  apiOptions: Omit<CreateApiOptions<AriBaseQuery, Definitions, ReducerPath, TagTypes>, 'baseQuery'>
  scopesObj: { scopes: string[] }
  serviceURL: string
  functionCode?: string
}

/**
 * A React hook that retrieves MSAL authentication claims asynchronously for a given scope set and returns the resulting claims array.
 *
 * Internally calls `getClaimsAsync` via `useEffect`; the value starts as undefined (rendered as `[]` due to the `useState` default) and is set once the async call resolves.
 *
 * @param scopes - MSAL login request scopes; defaults to `loginRequest`.
 * @returns An array of claims (or `undefined` while loading).
 *
 * @example
 * useGetClaims();
 *  // returns claims for the default loginRequest scope
 */
export const useGetClaims = (scopes = loginRequest) => {
  const [claims = [], setClaims] = useState<any>();
  const msContext = useMsal();
  useEffect(() => {
    getClaimsAsync(msContext, scopes).then((result) => {
      setClaims(result);
    });
  }, [msContext, scopes]);
  return claims;
};

/**
 * A factory function that builds an RTK Query API instance (via `createApi`) with MSAL token injection and a custom `baseQuery`.
 *
 * The `rawBaseQuery` fetches a token from MSAL on every request via `prepareHeaders`. The wrapper `baseQueryWithErrorHandling` additionally injects an optional `functionCode` param for Azure Function auth in dev, parses string error bodies into objects, and supports per-request `responseHandler` overrides. Callers must invoke `setMsContext` at app bootstrap to wire up the MSAL context.
 *
 * @param apiOptions - RTK Query API options (tagTypes, endpoints, etc.).
 * @param scopesObj - Object with a `scopes` array for MSAL token requests.
 * @param serviceURL - The base URL string for the API service.
 * @param functionCode - Optional Azure Function code appended to every request's query params in dev.
 * @returns An object with `hooks` (the RTK Query API with generated hooks), `setMsContext` (injects the MSAL context at runtime), `getRoles` (fetches user roles from MSAL), and `useApiGetRolesHook` (a React hook returning roles).
 */
export const AriApiQueryCreatorFetch = <
  Definitions extends EndpointDefinitions,
  ReducerPath extends string = string,
  TagTypes extends string = string,
>(
  { apiOptions, scopesObj, serviceURL, functionCode }: AriApiQueryCreatorProps<Definitions, ReducerPath, TagTypes>
) => {
  let msContext: IMsalContext;
  const setMsContext = (context: IMsalContext) => {
    msContext = context;
  };

  const rawBaseQuery = fetchBaseQuery({
    baseUrl: serviceURL,
    prepareHeaders: async (headers) => {
      const { getAccessTokenAsync } = getAuthTokens(msContext);
      const token = await getAccessTokenAsync(scopesObj);
      headers.set('Authorization', `Bearer ${token}`);
      const simulatedUser = getSimulatedUser();
      if (simulatedUser) {
        headers.set('X-Simulate-User', simulatedUser);
      }
      const simulatedRole = getSimulatedRole();
      if (simulatedRole) {
        headers.set('X-Simulate-Role', simulatedRole);
      }
      return headers;
    }
  });

  const baseQueryWithErrorHandling: AriBaseQuery = async (
    args,
    api,
    extraOptions
  ) => {
    // inject function code into params for every request in dev
    if (functionCode && typeof args === 'object') {
      (args as FetchArgs).params = { code: functionCode, ...((args as FetchArgs).params || {}) };
    }

    // Support per-request responseHandler to override default JSON parsing
    // Only capture custom function handlers; let built-in string handlers ('text', 'content-type') pass through
    let responseHandler: ((response: Response) => Promise<any> | any) | undefined;
    if (typeof args === 'object' && typeof (args as any).responseHandler === 'function') {
      responseHandler = (args as any).responseHandler;
      // remove custom field before passing to fetchBaseQuery
      delete (args as any).responseHandler;
    }

    const result: any = await rawBaseQuery(args, api, extraOptions);

    if (result.error && typeof result.error.data === 'string') {
      try {
        result.error.data = JSON.parse(result.error.data);
      } catch {
        result.error.data = { message: result.error.data };
      }
    }

    // If a custom response handler was provided and we have a Response object
    if (!result.error && responseHandler && result.meta?.response instanceof Response) {
      try {
        const processed = await responseHandler(result.meta.response);
        return { data: processed };
      } catch (e: any) {
        return { error: { status: 'CUSTOM_HANDLER_ERROR', data: e?.message || 'Failed to process response' } };
      }
    }

    return result;
  };

  const api = createApi({
    ...(apiOptions as CreateApiOptions<AriBaseQuery, Definitions, ReducerPath, TagTypes>),
    baseQuery: baseQueryWithErrorHandling,
  });

  const getRoles = async () => {
    const { roles }: any = await getClaimsAsync(msContext, scopesObj);
    return roles || [];
  };
  const useApiGetRolesHook = () => {
    return useGetClaims(scopesObj)?.roles || [];
  };

  return { hooks: api, setMsContext, getRoles, useApiGetRolesHook };
};

/**
 * Factory that builds an RTK Query API instance using an Axios-based base query with MSAL token injection.
 *
 * Similar to AriApiQueryCreatorFetch but uses a simpler Axios fetch path. Returns hooks, setMsContext, getRoles, and useApiGetRolesHook — callers must set the MSAL context before making API calls.
 */
export const AriApiQueryCreatorAxios = <
  Definitions extends EndpointDefinitions,
  ReducerPath extends string = string,
  TagTypes extends string = string,
>(
  { apiOptions, scopesObj, serviceURL }: AriApiQueryCreatorProps<Definitions, ReducerPath, TagTypes>
) => {
  let msContext: IMsalContext;
  const setMsContext = (context: IMsalContext) => {
    msContext = context;
  };

  const api = createApi({
    ...(apiOptions as CreateApiOptions<AriBaseQuery, Definitions, ReducerPath, TagTypes>),
    baseQuery: axiosBaseQuery({
      baseUrl: serviceURL,
      prepareHeaders: async (headers) => {
        const { getAccessTokenAsync } = getAuthTokens(msContext);
        const token = await getAccessTokenAsync(scopesObj);
        headers['Authorization'] = `Bearer ${token}`;
        return headers;
      }
    })
  });

  const getRoles = async () => {
    const { roles }: any = await getClaimsAsync(msContext, scopesObj);
    return roles || [];
  };

  const useApiGetRolesHook = () => {
    return useGetClaims(scopesObj)?.roles || [];
  };

  return { hooks: api, setMsContext, getRoles, useApiGetRolesHook };
};
